import { useCallback, useState } from 'react';
import { isAfter } from 'shared/dateFns';
import { useCurrentClient } from 'shared/context/ClientContext';
import { get as getFromStore, store as saveToStore, remove as removeFromStore } from 'shared/lstore';

/** Milliseconds in a single day (24h * 60m * 60s * 1000ms) */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Default number of days to snooze a banner */
const SNOOZE_DURATION_DAYS = 1;

/**
 * Type-safe banner IDs.
 * To add a new banner, add its ID to this array.
 */
export const BANNER_IDS = {
  nativePinterestConnector: 'nativePinterestConnector',
  nativeShopifyDeprecation: 'nativeShopifyDeprecation',
  reportingSolutionDeprecation: 'reportingSolutionDeprecation'
} as const;
export type BannerId = keyof typeof BANNER_IDS;

/**
 * Scope for banner dismissal:
 * - client: Workspace-specific (dismissed per workspace)
 * - app: Application-wide (dismissed for all workspaces)
 *
 * Note: this is always user-scoped scope, since the
 * dismissal key is stored in the user's local storage.
 */
export type BannerScope = 'client' | 'app';

/**
 * Configuration for banner dismissal behavior
 */
export interface BannerDismissConfig {
  /** Number of days to dismiss the banner (e.g., 1, 7, 30, 90) */
  days: number;
  /** Scope of dismissal - 'client' for workspace-specific, 'app' for all workspaces */
  scope: BannerScope;
}

/**
 * Typed structure for banner dismissal value stored in local storage
 */
export interface BannerDismissalValue {
  /** Timestamp when the banner was dismissed */
  createdAt: number;
  /** Timestamp when the dismissal expires */
  expirationTimestamp: number;
}

/**
 * Generate app-scoped dismissal key
 */
export const getAppDismissalKey = (bannerId: string): string => {
  return `banner:v1:${bannerId}:app`;
};

/**
 * Generate client-scoped dismissal key
 */
export const getClientDismissalKey = (bannerId: string, clientId: number): string => {
  return `banner:v1:${bannerId}:client:${clientId}`;
};

/**
 * Serialize dismissal value to JSON string for storage
 */
export const serializeDismissalValue = (value: BannerDismissalValue): string => {
  return JSON.stringify(value);
};

/**
 * Deserialize dismissal value from JSON string
 * Returns null if parsing fails or value is invalid
 */
export const deserializeDismissalValue = (serialized: string): BannerDismissalValue | null => {
  try {
    const parsed = JSON.parse(serialized) as BannerDismissalValue;
    if (typeof parsed.createdAt === 'number' && typeof parsed.expirationTimestamp === 'number' && !Number.isNaN(parsed.createdAt) && !Number.isNaN(parsed.expirationTimestamp)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Check if a dismissal key exists in storage and is still valid (not expired).
 * Returns true if valid dismissal exists, false otherwise.
 * Automatically cleans up expired or corrupted keys.
 */
const isDismissalKeyValidAndNotExpired = (key: string, now: number): boolean => {
  const serializedValue = getFromStore(key);
  if (serializedValue == null) return false;
  const dismissalValue = deserializeDismissalValue(serializedValue);
  if (dismissalValue == null) {
    removeFromStore(key);
    return false;
  }
  const isExpired = isAfter(new Date(now), new Date(dismissalValue.expirationTimestamp));
  if (isExpired) {
    removeFromStore(key);
    return false;
  }
  return true;
};

/** Check if banner is dismissed at app level */
const hasAppDismissal = (bannerId: string, now: number): boolean => {
  const appKey = getAppDismissalKey(bannerId);
  return isDismissalKeyValidAndNotExpired(appKey, now);
};

/** Check if banner is dismissed at client level */
const hasClientDismissal = (bannerId: string, clientId: number, now: number): boolean => {
  const clientKey = getClientDismissalKey(bannerId, clientId);
  return isDismissalKeyValidAndNotExpired(clientKey, now);
};

/**
 * Hook for managing banner dismissal and snooze functionality.
 *
 * @param bannerId - Type-safe banner ID from BANNER_IDS
 * @returns Object with shouldShow flag and dismiss/snooze functions
 *
 * @example
 * ```tsx
 * const { shouldShow, dismiss, snooze } = useBannerDismiss('attributionOnHomePage')
 *
 * // Dismiss for 90 days, client-scoped
 * dismiss({ days: 90, scope: 'client' })
 *
 * // Snooze (dismiss for 1 day, client-scoped)
 * snooze()
 * ```
 */
export const useBannerDismiss = (bannerId: BannerId): {
  id: BannerId;
  shouldShow: boolean;
  dismiss: (config: BannerDismissConfig) => void;
  snooze: () => void;
  isLoading: boolean;
} => {
  const {
    currentClient,
    isLoading: isClientLoading
  } = useCurrentClient();

  // Force re-render when dismissal state changes
  const [, setDismissalStateVersion] = useState(0);
  const determineShouldShow = (): boolean => {
    if (isClientLoading) return false;
    const now = Date.now();
    const isAppDismissed = hasAppDismissal(bannerId, now);
    const isClientDismissed = hasClientDismissal(bannerId, currentClient.id, now);
    return Boolean(currentClient?.id) && !isAppDismissed && !isClientDismissed;
  };
  const shouldShow = determineShouldShow();

  /**
   * Dismiss the banner for a specified number of days
   */
  const dismiss = useCallback((config: BannerDismissConfig) => {
    if (config.scope === 'app' || Boolean(currentClient.id)) {
      const dismissalKey = config.scope === 'app' ? getAppDismissalKey(bannerId) : getClientDismissalKey(bannerId, currentClient.id);
      const now_0 = Date.now();
      const expirationTimestamp = now_0 + config.days * ONE_DAY_MS;
      const dismissalValue: BannerDismissalValue = {
        createdAt: now_0,
        expirationTimestamp
      };
      saveToStore(dismissalKey, serializeDismissalValue(dismissalValue));
      setDismissalStateVersion(prev => prev + 1);
    }
  }, [bannerId, currentClient.id]);

  /**
   * Snooze the banner.
   * Effectively dismisses it for 1 day, scoped to the client.
   */
  const snooze = useCallback(() => {
    dismiss({
      days: SNOOZE_DURATION_DAYS,
      scope: 'client'
    });
  }, [dismiss]);
  return {
    id: bannerId,
    shouldShow,
    dismiss,
    snooze,
    isLoading: isClientLoading
  };
};